// Public consultation flow: use the anonymous client so chat context loads
// regardless of whether a staff member is logged in. See supabase.js.
import { supabasePublic as supabase } from '../lib/supabase';
import { getStoredUserId, getSessionId } from '../lib/supabase';

export class ChatContextService {
  // Get complete user context for chat agent
  static async getUserChatContext() {
    try {
      const userId = getStoredUserId();
      const sessionId = getSessionId();
      
      if (!userId) {
        return {
          user_id: null,
          session_id: sessionId,
          message: "Anonymous user - no consultation data available"
        };
      }

      // Query all user data
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select(`
          id,
          name,
          phone,
          session_id,
          created_at,
          user_consultations (
            id,
            skin_type,
            skin_sensitivity,
            additional_sensitivity,
            not_sensitive,
            has_active_consultation,
            analysis_results,
            created_at
          )
        `)
        .eq('id', userId)
        .single();

      if (userError || !userData) {
        return {
          user_id: userId,
          session_id: sessionId,
          error: "User data not found"
        };
      }

      // Get latest order if exists
      const { data: orderData } = await supabase
        .from('orders')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      // Format context for chat agent
      const consultation = userData.user_consultations?.[0];
      const context = {
        user_id: userData.id,
        session_id: userData.session_id,
        user_info: {
          name: userData.name || "غير محدد",
          phone: userData.phone || "غير محدد",
          member_since: userData.created_at
        },
        consultation_info: consultation ? {
          consultation_id: consultation.id,
          skin_type: consultation.skin_type || "غير محدد",
          skin_sensitivity: consultation.skin_sensitivity || [],
          additional_sensitivity: consultation.additional_sensitivity || "",
          not_sensitive: consultation.not_sensitive || false,
          has_active_consultation: consultation.has_active_consultation || false,
          analysis_results: consultation.analysis_results || null,
          consultation_date: consultation.created_at
        } : null,
        order_info: orderData ? {
          order_id: orderData.id,
          total_amount: orderData.total_amount,
          status: orderData.status,
          order_items: orderData.order_items,
          shipping_address: orderData.shipping_address,
          order_date: orderData.created_at
        } : null,
        context_timestamp: new Date().toISOString()
      };

      return context;
    } catch (error) {
      console.error('Error getting chat context:', error);
      return {
        user_id: getStoredUserId(),
        session_id: getSessionId(),
        error: error.message
      };
    }
  }

  // Get current step context
  static getCurrentStepContext(currentStep, orderData, displayRoutines) {
    return {
      current_step: currentStep,
      step_name: this.getStepName(currentStep),
      order_data: currentStep === 'checkout' ? {
        selected_products: displayRoutines,
        shipping_info: orderData,
        total_amount: displayRoutines?.totalPrice
      } : null
    };
  }

  static getStepName(step) {
    const stepNames = {
      1: "تحميل الصورة",
      2: "معلومات المستخدم",
      3: "تحليل الجلد",
      'checkout': "الدفع والشحن",
      'completion': "اكتمال الطلب"
    };
    return stepNames[step] || "غير محدد";
  }

  // Helper method to format user context for display in chat
  static formatContextForAgent(context) {
    let message = "معلومات المستخدم:\n\n";
    
    if (context.user_info) {
      message += `👤 الاسم: ${context.user_info.name}\n`;
      message += `📱 الهاتف: ${context.user_info.phone}\n\n`;
    }
    
    if (context.consultation_info) {
      message += "🔬 معلومات الاستشارة:\n";
      message += `• نوع البشرة: ${context.consultation_info.skin_type}\n`;
      if (context.consultation_info.skin_sensitivity?.length > 0) {
        message += `• حساسية البشرة: ${context.consultation_info.skin_sensitivity.join(', ')}\n`;
      }
      if (context.consultation_info.additional_sensitivity) {
        message += `• حساسية إضافية: ${context.consultation_info.additional_sensitivity}\n`;
      }
      message += "\n";
    }
    
    if (context.order_info) {
      message += "🛍️ معلومات الطلب:\n";
      message += `• رقم الطلب: ${context.order_info.order_id}\n`;
      message += `• المبلغ الإجمالي: ${context.order_info.total_amount}\n`;
      message += `• حالة الطلب: ${context.order_info.status}\n`;
      if (context.order_info.shipping_address) {
        message += `• عنوان الشحن: ${context.order_info.shipping_address.address || 'غير محدد'}\n`;
      }
    }
    
    return message;
  }
}
